/* @flow */
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import cx from 'classnames';
import { DropTarget } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';
import { OrderedMap } from 'immutable';
import * as React from 'react';
import { keys } from 'lodash';
import invariant from 'invariant';
import { compose } from 'react-apollo';

import Button from 'components/Button';
import Buttons from 'components/Buttons';
import Chat from 'entities/Chat';
import ChatActions from 'chat/actions/ChatActions';
import Composer from 'chat/components/Composer';
import ComposerFilePreview from 'chat/components/ComposerFilePreview';
import ConfirmDialog from 'components/ConfirmDialog';
import DateConstants from 'constants/DateConstants';
import DropZoneModal from 'components/DropZoneModal';
import Glyph from 'components/Glyph';
import Image from 'components/Image';
import KeyConstants from 'constants/KeyConstants';
import LanguageHoverCard from 'chat/components/LanguageHoverCard';
import LayerTrigger from 'components/LayerTrigger';
import MediaObject from 'components/MediaObject';
import MessageComposerActions from 'chat/actions/MessageComposerActions';
import MessageComposerStore from 'chat/stores/MessageComposerStore';
import SettingsStore from 'stores/SettingsStore';
import Timing from 'lib/Timing';
import TranslationActions from 'actions/TranslationActions';
import ReduxUploadButton from 'components/ReduxUploadButton';
import { mountModal } from 'lib/modal';
import ComposerAlert from './ComposerAlert';
import withWidth from 'lib/withWidth';
import dateTimeFormat from 'lib/dateTimeFormat';
import {
  getFilesArrayFromFileUploads,
  getFirstFileId,
  getFirstFile,
  uploadFile,
} from 'lib/fileUploadHelper';
import {
  fetchFileById,
  loadThumbnail,
  removeFile,
  resetFile,
  uploadRequest,
} from 'reducers/fileUploads';
import errorLogger from 'utils/errorLogger';

import './styles/MessageComposer.scss';

const OFFICE_HOURS_NOTICE_TIMEOUT = 60 * 60 * 1000;
const AFTER_HOURS_NOTICE_TIMEOUT = 60 * 60 * 1000;
export const CHARACTER_LIMIT = 1500;
const UI_CONTEXT = 'chat_composer';

type Props = {
  isNewChat?: ?boolean,
  chat: Chat,
  onMessageSend: Function,
  width: number,
  openSideBar?: ?Function,
  hideComposerAlert?: ?boolean,
  className?: ?string,

  /* redux props */
  fileUploads: OrderedMap<*, *>,
  fetchFileById: Function,
  removeFile: Function,
  resetFile: Function,
  uploadRequest: Function,
  loadThumbnail: Function,

  /* dnd props */
  connectDropTarget: Function,
  isDraggingFile?: ?boolean,
};

type State = {
  composerError: boolean,
  latestTranslation: string,
  text: string,
  translationBoxOpened: boolean,
};

const initialState: State = {
  composerError: false,
  latestTranslation: '',
  text: '',
  translationBoxOpened: false,
};

class MessageComposer extends React.Component<Props, State> {
  static MEDIUM = 750;
  static SMALL = 650;
  static XSMALL = 550;
  static XXSMALL = 450;

  state = initialState;

  componentWillMount() {
    if (this.props.chat.id) {
      // $FlowFixMe
      const newState = MessageComposerStore.getComposerStates((this.props.chat.id: string));
      if (newState) {
        this.setState(newState);
      }
    }
  }

  componentWillUpdate(nextProps: Object) {
    const currentChatId = this.props.chat.id;
    const nextChatId = nextProps.chat.id;
    const chatChanged = nextChatId !== currentChatId;

    if (chatChanged) {
      // When switching chats, we save the composer state for persisted chats to be restored if we come back to this chat.
      if (!this.props.chat.isNew()) {
        const stateBeforeTimeout = this.state;
        // Timeout to place action at end of dispatch stack
        setTimeout(() => {
          MessageComposerActions.saveState(currentChatId, stateBeforeTimeout);
        }, 0);
      }

      this.removeAllFiles();
      if (this.refs.composer) {
        this.refs.composer.clear();
      }

      if (nextChatId) {
        const state = MessageComposerStore.getComposerStates(nextChatId) || initialState;
        this.setState({ ...state });
      }
    }
  }

  componentWillUnmount() {
    if (!this.props.chat.isNew()) {
      setTimeout(() => {
        MessageComposerActions.saveState(this.props.chat.id, this.state);
      }, 0);
    }
    this.removeAllFiles();
  }

  removeAllFiles = () => {
    const { chat, fileUploads } = this.props;
    fileUploads.keySeq().forEach(filename => this.props.removeFile(chat.id, filename));
  };

  getDropzoneId = () => {
    if (this.props.isNewChat) {
      return 'newChat';
    } else {
      return this.props.chat.id;
    }
  };

  getTranslationBox = () => {
    if (!this.state.translationBoxOpened) {
      return null;
    }

    const languages = SettingsStore.getLanguages().primary;

    if (!languages) {
      return null;
    }

    const buttons = [];

    let displayedMax = keys(languages).length;
    let displayed = 0;

    if (this.props.width < MessageComposer.XXSMALL) {
      displayedMax -= 5;
    } else if (this.props.width < MessageComposer.XSMALL) {
      displayedMax -= 3;
    } else if (this.props.width < MessageComposer.SMALL) {
      displayedMax -= 2;
    } else if (this.props.width < MessageComposer.MEDIUM) {
      displayedMax -= 1;
    }

    for (const lang in languages) {
      displayed += 1;
      if (displayed <= displayedMax) {
        buttons.push(
          <Button
            key={`translate-${lang}`}
            eventContextName={UI_CONTEXT}
            eventElementName="translate_button"
            eventProperties={{
              language_code: lang,
            }}
            onClick={() => this.translateTo(lang)}>
            {languages[lang]}
          </Button>
        );
      }
    }

    buttons.push(
      <LayerTrigger
        key="show-more"
        layer={
          <LanguageHoverCard
            eventContextName={UI_CONTEXT}
            onLanguagePicked={lang => this.translateTo(lang)}
          />
        }
        placement="top"
        trigger="click"
        alignment="middle">
        <Button
          eventContextName={UI_CONTEXT}
          eventElementName="show_more_languages_button"
          level="secondary"
          color="thunder"
          size="xsmall"
          aria-label="More languages">
          <Glyph name="ellipses" solo />
        </Button>
      </LayerTrigger>
    );

    return (
      <div className="MessageComposer-translationBox">
        <div className="MessageComposer-translationBox-buttons">
          <Buttons color="thunder" group level="secondary" size="xsmall">
            {buttons}
          </Buttons>
        </div>

        <Button
          className="MessageComposer-translationBox-closeButton"
          eventContextName={UI_CONTEXT}
          eventElementName="close_translation_box"
          onClick={this.toggleTranslationBoxOpened}
          level="text"
          aria-label="Close">
          <Glyph name="close-circle" color="brand" size="xlarge" solo />
        </Button>
      </div>
    );
  };

  toggleTranslationBoxOpened = () => {
    this.setState(({ translationBoxOpened }) => ({ translationBoxOpened: !translationBoxOpened }));
  };

  allowSendAfterHours = null;

  allowSendOutsideOfficeHours = {};

  chatState = {};

  handleTextChange = (event: SyntheticEvent<>) => {
    // $FlowIssue
    const text = event.target.value;

    if (text === this.state.text) {
      return;
    }

    this.setState({
      text,
      composerError: text.trim().length > CHARACTER_LIMIT,
    });
  };

  confirmSendMessageAfterHours = () => {
    const timeWarning = Timing.isEarly() ? 'It’s pretty early.' : 'It’s pretty late.';
    const question = 'Send this message anyway?';
    const title = (
      <div>
        <Image
          className="MessageComposer-confirmImage"
          src={require('assets/images/after-hours.svg')}
        />
        <p>
          {timeWarning} {question}
        </p>
      </div>
    );
    mountModal(
      <ConfirmDialog
        title={title}
        eventContext="ChatMessageComposer"
        size="xsmall"
        onConfirm={() => {
          this.allowSendAfterHours = setTimeout(() => {
            this.allowSendAfterHours = null;
          }, AFTER_HOURS_NOTICE_TIMEOUT);
          this.sendMessage();
        }}
      />,
      this
    );
  };

  confirmSendMessageOutsideOfOfficeHours = () => {
    const { chat } = this.props;
    let otherChatMember = null;
    let nextAvailability = null; // XXX weird use of some() since the return value isn't used
    chat.getOtherChatMembers().some(member => {
      const officeHours = member.getOfficeHours();
      if (officeHours && !officeHours.isAvailableNow()) {
        otherChatMember = member;
        nextAvailability = officeHours.getNextAvailability();
        return true;
      }
      return false;
    });
    invariant(nextAvailability, 'unable to determine nextAvailability');
    const startTime = nextAvailability.getStartTime();
    const date = new Date();
    const diff = (nextAvailability.getDay() + 7 - date.getDay()) % 7;
    date.setHours(Math.floor(startTime / 3600));
    date.setMinutes((startTime % 3600) / 60);
    const day = diff === 1 ? 'tomorrow' : DateConstants.WEEKDAYS[nextAvailability.getDay()];
    const time = dateTimeFormat(date, {
      hour: 'numeric',
      minute: '2-digit',
    });
    const otherChatMemberName = otherChatMember ? otherChatMember.getName() : '???';
    mountModal(
      <ConfirmDialog
        size="xsmall"
        glyph="clock-bubble"
        glyphColor="violet"
        title={`Uh-oh! ${otherChatMemberName} won’t be back until ${day} at ${time}.`}
        subtext={`Office hours for ${otherChatMemberName} won’t start until ${day} at ${time}, so they may not receive your message right now. Send anyway?`}
        onConfirm={() => {
          this.allowSendOutsideOfficeHours[chat.id] = setTimeout(() => {
            delete this.allowSendOutsideOfficeHours[chat.id];
          }, OFFICE_HOURS_NOTICE_TIMEOUT);
          this.sendMessage();
        }}
      />,
      this
    );
  };

  focus = () => {
    if (this.refs.composer) {
      this.refs.composer.focus();
    }
  };

  handleEnableRepliesButtonClick = (event: Event) => {
    event.preventDefault();
    ChatActions.enableReplies(this.props.chat);
  };

  isChatEnabled = () => {
    return this.props.chat.isPresent() && !this.props.chat.isUnsubscribed();
  };

  isValidMessage = () => {
    const { text } = this.state;
    const { fileUploads } = this.props;

    const allUploadsComplete = fileUploads.every(file => {
      return file.progressPercentage === 100;
    });
    const hasText = text.trim().length > 0;

    return allUploadsComplete && (hasText || !fileUploads.isEmpty());
  };

  getPlaceholder = () => {
    const chatTitle = this.props.chat.getTitle();
    if (chatTitle) {
      return `Message ${chatTitle}`;
    } else {
      return 'Type a message';
    }
  };

  sendMessage = () => {
    const { chat, fileUploads } = this.props;
    if (chat.isOutsideOfOtherOfficeHours() && !this.allowSendOutsideOfficeHours[chat.id]) {
      this.confirmSendMessageOutsideOfOfficeHours();
    } else if ((Timing.isLate() || Timing.isEarly()) && !this.allowSendAfterHours) {
      this.confirmSendMessageAfterHours();
    } else {
      this.props.onMessageSend(this.state.text, getFilesArrayFromFileUploads(fileUploads));
      this.refs.composer.clear();
      this.setState({
        text: '',
      });
      this.removeAllFiles();
    }
  };

  handleReturn = (event: SyntheticKeyboardEvent<>): boolean => {
    if (event.keyCode === KeyConstants.ENTER && !event.shiftKey) {
      this.handleSubmit(event);
      return true;
    }
    return false;
  };

  handleSubmit = (event: SyntheticEvent<>) => {
    event.preventDefault();
    this.submitComposer();
  };

  submitComposer = () => {
    if (!this.submitDisabled()) {
      this.sendMessage();
    }
  };

  submitDisabled = () => {
    return (
      this.state.translationBoxOpened ||
      this.state.composerError ||
      !this.isChatEnabled() ||
      !this.isValidMessage()
    );
  };

  translateTo = (lang: string) => {
    TranslationActions.translate(this.state.text, lang)
      .then(response => {
        this.setState({
          text: response.translation,
          latestTranslation: lang,
          translationBoxOpened: false,
        });
      })
      .catch(errorLogger('TranslationActions.translate failed'));
  };

  retryUpload = (filename: string) => {
    const { chat, fileUploads } = this.props;
    this.props.resetFile(chat.id, filename);
    const file = fileUploads.get(filename);
    invariant(file, 'MessageComposer retryUpload missing file');
    this.props.uploadRequest(chat.id, filename, file.blob);
  };

  renderMessageComposer = () => {
    const { composerError, translationBoxOpened, latestTranslation } = this.state;
    if (!this.props.chat.canSend()) {
      return null;
    }
    const { chat, fileUploads, isDraggingFile } = this.props;
    let errorMessage = null;
    if (composerError) {
      errorMessage = (
        <div className="MessageComposer-error">
          Sorry, you can’t send more than {CHARACTER_LIMIT} characters
        </div>
      );
    }
    const attachButton = (
      <ReduxUploadButton className="MessageComposer-attach" level="text" fileContextKey={chat.id}>
        <Glyph name="paperclip" className="MessageComposer-actionGlyph" />
        Attach
      </ReduxUploadButton>
    );
    const translateButton = (
      <Button
        className="MessageComposer-translate"
        disabled={this.state.text.length < 1}
        eventContextName={UI_CONTEXT}
        eventElementName={translationBoxOpened ? 'hide_translation_box' : 'show_translation_box'}
        onClick={() =>
          this.setState({
            translationBoxOpened: !translationBoxOpened,
          })
        }
        level="text">
        <Glyph name="globe" className="MessageComposer-actionGlyph" />
        <span>Translate</span>
      </Button>
    );

    const firstFileId = getFirstFileId(fileUploads);
    const uploadRequestData = getFirstFile(fileUploads);
    const removeFirstFile = () => firstFileId && this.props.removeFile(chat.id, firstFileId);
    const retryFirstFile = () => firstFileId && this.retryUpload(firstFileId);

    const filePreview = uploadRequestData ? (
      <div className="MessageComposer-preview">
        <ComposerFilePreview
          className={cx({
            draggingFile: isDraggingFile,
          })}
          progress={uploadRequestData.progressPercentage}
          thumbnail={uploadRequestData.thumbnail}
          type={uploadRequestData.file && uploadRequestData.file.getContentType()}
          error={uploadRequestData.isError}
          onDelete={removeFirstFile}
          onRetry={retryFirstFile}
          file={uploadRequestData.file}
        />
      </div>
    ) : null;

    const eventProperties = {};
    if (latestTranslation) {
      eventProperties.language_code = latestTranslation;
    }
    const otherMembersPlaceholderText = this.getPlaceholder();
    return (
      <div className="MessageComposer-composerContainer" data-test-selector="message-composer">
        <MediaObject figure="right" gutter="xsmall">
          <div>
            <Composer
              autogrow
              autoFocus
              constrainMaxHeight
              ref="composer"
              className="MessageComposer-composer"
              onChange={this.handleTextChange}
              onReturnKeyPress={this.handleReturn}
              error={composerError}
              value={this.state.text}
              placeholder={otherMembersPlaceholderText}
              disabled={!this.isChatEnabled()}>
              {filePreview}
              {this.getTranslationBox()}
            </Composer>
            {errorMessage}
            <div className="MessageComposer-actions">
              {attachButton}
              {translateButton}
            </div>
          </div>
          <Button
            className="MessageComposer-sendButton"
            data-test-selector="message-composer-send-button"
            eventContextName={UI_CONTEXT}
            eventElementName="send_button"
            eventProperties={eventProperties}
            level="primary"
            size="small"
            onClick={this.handleSubmit}
            disabled={this.submitDisabled()}>
            Send
          </Button>
        </MediaObject>
      </div>
    );
  };

  render() {
    const { fileUploads, isDraggingFile, connectDropTarget } = this.props;
    const dropzone = (
      <div>
        <DropZoneModal
          titleDrop="Drop a file here"
          isFileUploaded={!fileUploads.isEmpty()}
          isHovering
        />
      </div>
    );
    const composerAlert = (
      <ComposerAlert chat={this.props.chat} openSideBar={this.props.openSideBar} />
    );
    return connectDropTarget(
      <div className={cx('MessageComposer', this.props.className)}>
        {isDraggingFile && dropzone}
        {!this.props.hideComposerAlert && composerAlert}
        {this.renderMessageComposer()}
      </div>
    );
  }
}

export { MessageComposer };

const type = NativeTypes.FILE;

const dndEvents = {
  drop: (props, monitor) => {
    const file = monitor.getItem().files[0];
    uploadFile(props.chat.id, file, props.uploadRequest, props.loadThumbnail);
  },
  canDrop: props => {
    return props.fileUploads.isEmpty(); // can only attach one file at a time
  },
};

const collect = (connector, monitor) => ({
  connectDropTarget: connector.dropTarget(),
  isDraggingFile: monitor.isOver(),
});

const mapStateToProps = (state: Object, props: Object) => {
  const { fileUploads } = state;
  const fileGroup = fileUploads.get(props.chat.id);
  return {
    fileUploads: fileGroup || new OrderedMap(),
  };
};

const mapDispatchToProps = (dispatch: Function) =>
  // $FlowFixMe
  bindActionCreators(
    {
      fetchFileById,
      removeFile,
      resetFile,
      uploadRequest,
      loadThumbnail,
    },
    dispatch
  );

// Expose for testing
export const ReduxMessageComposer = connect(
  mapStateToProps,
  mapDispatchToProps
)(MessageComposer);

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  withWidth,
  DropTarget(type, dndEvents, collect)
)(MessageComposer);
